import { ArrowUpRight, MapPin, Calendar } from "lucide-react";

/* ============================================================
 * EVENTS — Inaugural DCYB community event, Cannes 2026
 * Datacloud Global Congress side gathering
 * ============================================================ */

const PHOTOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/pahk8erg_IMG_4911.jpeg",
    alt: "DCYB attendees networking under the awning beside the event easel, Cannes 2026.",
    code: "01 / DAYTIME",
    caption: "Welcome reception on the Cannes seafront during Datacloud Global Congress 2026.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/d5770x1s_IMG_4933.jpeg",
    alt: "DCYB poster lit pink at night with the Mediterranean and yacht harbor behind it.",
    code: "02 / NIGHTFALL",
    caption: "The evening program continues under the lights at the beach club.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/ufc4adjl_IMG_2547.jpeg",
    alt: "DCYB CEO Luke Adams speaking with attendees beside the DCYB easel at the inaugural event, Cannes 2026.",
    code: "03 / THE CLASS OF 2026",
    caption: "Our CEO, Luke Adams, building the community.",
  },
];

export default function Events() {
  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative border-t hairline bg-[#F4F5F7] overflow-hidden"
    >
      {/* Faint scanlines & grid wash to match the chrome theme */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #0F1117 0 1px, transparent 1px 4px)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Eyebrow strip */}
        <div className="flex items-center justify-between mb-10 md:mb-14">
          <span
            className="font-mono-label text-[11px] tracking-[0.18em] text-[#71747B]"
            data-testid="events-eyebrow"
          >
            / Events
          </span>
          <span className="font-mono-label text-[10px] text-[#71747B] hidden md:inline-flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
            FIRST OF MANY
          </span>
        </div>

        {/* Headline + lede */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 lg:mb-20">
          <div className="lg:col-span-7">
            <h2
              data-testid="events-headline"
              className="font-dcyb text-[2.4rem] sm:text-5xl lg:text-[5.2rem] leading-[0.92]"
            >
              <span className="text-dcyb-chrome">
                The First
                <br />
                of Many
              </span>
            </h2>
          </div>

          <div className="lg:col-span-5 lg:pt-4">
            <p
              className="text-base sm:text-lg text-[#3D4049] leading-relaxed"
              data-testid="events-lede"
            >
              Proud to put this event together. It is the first of many, so do
              not miss the next one. Our team learned something from every
              person who walked through the door. The conversations were
              extraordinary, and a reminder of why DCYB exists.
            </p>
            <p className="mt-4 text-base sm:text-lg text-[#3D4049] leading-relaxed">
              DCYB is growing fast. We will replicate this in hubs worldwide and
              collaborate at conferences everywhere. Reach out to learn more.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#book"
                data-testid="events-book-cta"
                className="chrome-btn-dark inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                Reach out
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href="#engage"
                data-testid="events-engage-cta"
                className="chrome-btn inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold"
              >
                Get on the list
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Photo grid — editorial vertical flow */}
        <div className="space-y-3 md:space-y-4">
          {/* 3-up grid of event photos */}
          <p aria-hidden="true" className="md:hidden font-mono-label text-[10px] !tracking-normal text-[#71747B]">Swipe for more photos →</p>
          <div data-testid="events-photo-strip" className="flex md:grid md:grid-cols-3 gap-3 md:gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory scroll-px-6 -mx-6 px-6 md:mx-0 md:px-0 pb-2 md:pb-0 [&>*]:shrink-0 [&>*]:w-[82%] md:[&>*]:w-auto [&>*]:snap-start">
            <figure
              className="chrome-surface border hairline overflow-hidden group flex flex-col"
              data-testid="events-photo-1"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-[#0F1117]">
                <img
                  src={PHOTOS[0].src}
                  alt={PHOTOS[0].alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="p-3 md:p-4 border-t hairline bg-white/70 backdrop-blur flex-1">
                <span className="font-mono-label text-[10px] text-[#71747B]">
                  {PHOTOS[0].code}
                </span>
                <p className="text-[12px] md:text-[13px] text-[#3D4049] leading-snug mt-1">
                  {PHOTOS[0].caption}
                </p>
              </figcaption>
            </figure>

            <figure
              className="chrome-surface border hairline overflow-hidden group flex flex-col"
              data-testid="events-photo-2"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-[#0F1117]">
                <img
                  src={PHOTOS[1].src}
                  alt={PHOTOS[1].alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="p-3 md:p-4 border-t hairline bg-white/70 backdrop-blur flex-1">
                <span className="font-mono-label text-[10px] text-[#71747B]">
                  {PHOTOS[1].code}
                </span>
                <p className="text-[12px] md:text-[13px] text-[#3D4049] leading-snug mt-1">
                  {PHOTOS[1].caption}
                </p>
              </figcaption>
            </figure>

            <figure
              className="chrome-surface border hairline overflow-hidden group flex flex-col"
              data-testid="events-photo-3"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-[#0F1117]">
                <img
                  src={PHOTOS[2].src}
                  alt={PHOTOS[2].alt}
                  loading="lazy"
                  className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="p-3 md:p-4 border-t hairline bg-white/70 backdrop-blur flex-1">
                <span className="font-mono-label text-[10px] text-[#71747B]">
                  {PHOTOS[2].code}
                </span>
                <p className="text-[12px] md:text-[13px] text-[#3D4049] leading-snug mt-1">
                  {PHOTOS[2].caption}
                </p>
              </figcaption>
            </figure>
          </div>
        </div>

        {/* Event meta strip */}
        <div className="mt-10 md:mt-14 border hairline chrome-surface px-5 md:px-8 py-5 grid grid-cols-1 md:grid-cols-4 gap-5 md:gap-8">
          <div>
            <div className="font-mono-label text-[10px] text-[#71747B] mb-1">
              / EDITION
            </div>
            <div className="font-display text-lg font-bold text-[#0F1117]">
              Inaugural
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 text-[#52555B] mt-1 shrink-0" />
            <div>
              <div className="font-mono-label text-[10px] text-[#71747B] mb-1">
                / LOCATION
              </div>
              <div className="font-display text-lg font-bold text-[#0F1117]">
                Cannes, France
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Calendar className="w-4 h-4 text-[#52555B] mt-1 shrink-0" />
            <div>
              <div className="font-mono-label text-[10px] text-[#71747B] mb-1">
                / ALONGSIDE
              </div>
              <div className="font-display text-lg font-bold text-[#0F1117]">
                Datacloud Global Congress
              </div>
            </div>
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-[#71747B] mb-1">
              / NEXT
            </div>
            <div className="font-display text-lg font-bold text-[#0F1117] inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
              Announcing soon
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
